import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ClaimApi } from '@eis/claimservice-sdk';
import type { FilterCatalog } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { unwrap } from './result';

/**
 * CLAIMS (SDD-018 §9): claimservice's REST API, the image run as is (C-7). The console lists, reads
 * and creates claims — full handling stays in the suite (C-12). A claim is created only after
 * `useCoverageAtDate` answered COVERED (C-11); that check is the screen's, not this layer's.
 */

/** The claim statuses claims-bridge ensures per tarif (claimservice.default-statuses.json). */
export const CLAIM_STATUSES = ['reported', 'open', 'in_progress', 'closed', 'rejected'] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

/** The semantic tone a claim status is drawn with (the fixture's `token`: SDD-018 §9). */
export type ClaimStatusTone = 'info' | 'warning' | 'success' | 'neutral';

/**
 * claimservice.default-statuses.json's `token` per status — one vocabulary for the claims module
 * and the insurance tabs. A status a tenant added in the suite is not here and draws `neutral`.
 */
export const CLAIM_STATUS_TONES: Readonly<Record<ClaimStatus, ClaimStatusTone>> = {
  reported: 'info',
  open: 'warning',
  in_progress: 'info',
  closed: 'success',
  rejected: 'neutral',
};

export function claimStatusTone(status: string): ClaimStatusTone {
  return (CLAIM_STATUS_TONES as Readonly<Record<string, ClaimStatusTone | undefined>>)[status] ?? 'neutral';
}

/**
 * The claims list's filter catalog. claimservice serves none (`:filterCatalog` is this platform's
 * standard, SDD-017), so the console states the one its query layer implements — the
 * `filterableColumns` of claimservice's entity query (docs/domain/claims/FACTS.md §4.3), with the
 * operators its parser accepts. Labels are the claims module's keys.
 */
export const CLAIM_FILTER_CATALOG: FilterCatalog = {
  fields: [
    {
      key: 'policyCode',
      group: 'SYSTEM',
      type: 'STRING',
      labelKey: 'claims.field.policyCode',
      operators: ['==', '!=', '=@', '=()'],
      sortable: false,
      identifier: true,
    },
    {
      key: 'accountCode',
      group: 'SYSTEM',
      type: 'STRING',
      labelKey: 'claims.field.accountCode',
      operators: ['==', '!=', '=@', '=()'],
      sortable: true,
    },
    {
      key: 'status',
      group: 'SYSTEM',
      type: 'ENUM',
      labelKey: 'claims.field.status',
      operators: ['==', '!=', '=()', '!()'],
      sortable: true,
      values: CLAIM_STATUSES.map((value) => ({ value, labelKey: `claims.status.${value}` })),
    },
    {
      key: 'claimType',
      group: 'SYSTEM',
      type: 'STRING',
      labelKey: 'claims.field.claimType',
      operators: ['==', '!=', '=()'],
      sortable: true,
    },
    {
      key: 'damageDate',
      group: 'SYSTEM',
      type: 'DATE',
      labelKey: 'claims.field.damageDate',
      operators: ['==', '>', '<', '>=', '<='],
      sortable: true,
    },
    {
      key: 'notificationDate',
      group: 'SYSTEM',
      type: 'DATE',
      labelKey: 'claims.field.notificationDate',
      operators: ['==', '>', '<', '>=', '<='],
      sortable: true,
    },
  ],
};

/** claimservice's `order` grammar is `field:asc|desc`; the console's lists speak `-field`. */
export function claimOrder(order: string | undefined): string | undefined {
  if (order === undefined || order === '') return undefined;
  return order
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => (part.startsWith('-') ? `${part.slice(1)}:desc` : `${part}:asc`))
    .join(',');
}

/** One claim (its `claimPartners` as the image returns them). */
export function useClaim(code: string): UseQueryResult<ClaimApi.GetClaimResponseClass> {
  const { claims } = useApiClients();
  return useQuery({
    queryKey: apiKeys.claims.claim(code),
    queryFn: () =>
      unwrap(
        claims.GET('/claimservice/v1/claims/{code}', {
          params: { path: { code } },
        }),
      ),
    enabled: code !== '',
  });
}

/** What creating a claim takes: the claim, and the liable party linked as claim partner. */
export interface CreateClaimVariables {
  readonly claim: ClaimApi.CreateClaimRequestDto;
  /** The liable party's partner code (`pr_…`) and the claim partner role's code, when known. */
  readonly liableParty?: { readonly partnerCode: string; readonly claimPartnerRoleCode: string };
}

/**
 * Creates a claim, then links the liable party (C-11: "Versicherungsnehmer"). Two calls, because
 * claimservice has two endpoints; a failing second call leaves the claim created and says so.
 */
export function useCreateClaim(): UseMutationResult<
  ClaimApi.CreateClaimResponseClass,
  Error,
  CreateClaimVariables
> {
  const { claims } = useApiClients();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ claim, liableParty }) => {
      const created = await unwrap(claims.POST('/claimservice/v1/claims', { body: claim }));
      const code = created.claim.code;
      if (liableParty !== undefined) {
        await unwrap(
          claims.POST('/claimservice/v1/claims/{claimCode}/partner', {
            params: { path: { claimCode: code } },
            body: {
              claimCode: code,
              partnerCode: liableParty.partnerCode,
              claimPartnerRoleCode: liableParty.claimPartnerRoleCode,
            },
          }),
        );
      }
      return created;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiKeys.claims.list() });
    },
  });
}

/** The claim partner roles of a product slug — where "Versicherungsnehmer" is found. */
export function useClaimPartnerRoles(
  productSlug: string,
): UseQueryResult<ClaimApi.ListClaimPartnerRolesResponseClass> {
  const { claims } = useApiClients();
  return useQuery({
    queryKey: [...apiKeys.claims.root, 'partner-roles', productSlug],
    queryFn: () =>
      unwrap(
        claims.GET('/claimservice/v1/claim-partner-roles', {
          params: { query: { pageSize: 50, filters: `productSlug==${productSlug}` } },
        }),
      ),
    enabled: productSlug !== '',
    staleTime: 5 * 60_000,
  });
}
